import React, { useEffect, useMemo, useState } from 'react';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaEye,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCloudUploadAlt
} from 'react-icons/fa';
import {
  FiSearch,
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiRefreshCw,
  FiSettings
} from 'react-icons/fi';
import { Pagination } from '../../utils/pagination.utils';
import { getAssociationDisplayLabel } from '../../utils/association.utils';
import { useAuth } from '../../provider/auth.provider';
import { Link } from 'react-router-dom';
import { SelectWithSearch, type SelectOption } from '../../utils/select-with-search';
import { ImportUsersOffcanvas } from './import';

interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
  project?: {
    id: number;
    project_name: string;
  } | null;
}

  interface User {
    id: number;
    email: string;
    username: string;
    device_version?: string | null;
    password_hash: string;
    employee_code?: string
    email_verified: boolean;
  email_verification_token: string | null;
  email_verification_expires_at: string | null;
  phone_number: string | null;
  country_code: string | null;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  gender: string | null;
  profile_picture: string | null;
  address: string | null;
  city: string | null;
  state: string | null | { id: number; state_name: string; circle_name: string };
  country: string | null;
  pincode: string | null;
  role_id: number;
  association_type: string;
  status: string;
  employment_status: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  state_circle?: string;
  district?: string | { id: number; name: string };
  state_circle_id?: number;
  district_id?: number;
  reporting_latitude?: string | null;
  reporting_longitude?: string | null;
  project_id?: number | null;
  department_id?: number | null;
  user_projects?: UserProject[];
  role: {
    id: number;
    name: string;
  };
  department?: {
    id: number;
    name: string;
  } | null;
  designation?: {
    id: number
    name: string
  };
  project_manager?: {
    id?: number;
    employee_code?: string | null;
    username?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    designation?: {
      id?: number;
      name?: string;
    } | null;
  } | null;
}

interface Role {
  id: number;
  name: string;
}

export interface Filters {
  search?: string;
  name?: string;
  email?: string;
  username?: string;
  role_id?: string;
  status?: string;
  employment_status?: string;
  state_id?: string;
  district_id?: string;
  gender?: string;
  association_type?: string;
  employee_code?: string;
  created_from?: string;
  created_to?: string;
  is_field_user?: string;
  phone_number?: string;
  role?: string;
}

type FiltersUpdater = Filters | ((prev: Filters) => Filters);

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  onFiltersChange: (filters: FiltersUpdater) => void;
  onApply: () => void;
  onReset: () => void;
  roles: Role[];
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  roles
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);
  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        id: role.id,
        label: role.name,
        value: role.id
      })),
    [roles]
  );

  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { id: 1, label: 'Verified', value: 'verified' },
      { id: 2, label: 'Pending', value: 'pending' },
      { id: 3, label: 'Initiated', value: 'initiated' },
      { id: 4, label: 'Suspended', value: 'suspended' },
      { id: 5, label: 'Deactivated', value: 'deactivated' }
    ],
    []
  );
  
  const employmentStatusOptions = useMemo<SelectOption[]>(
    () => [
      { id: 1, label: 'Working', value: 'working' },
      { id: 2, label: 'Not Working', value: 'not_working' },
      { id: 3, label: 'Left', value: 'left' }
    ],
    []
  );

  const selectedRole = useMemo(() => {
    if (!filters.role_id) return null;
    return roleOptions.find((option) => String(option.value) === String(filters.role_id)) || null;
  }, [filters.role_id, roleOptions]);

  const selectedStatus = useMemo(() => {
    if (!filters.status) return null;
    return statusOptions.find((option) => option.value === filters.status) || null;
  }, [filters.status, statusOptions]);

  const selectedEmploymentStatus = useMemo(() => {
    if (!filters.employment_status) return null;
    return employmentStatusOptions.find((option) => option.value === filters.employment_status) || null;
  }, [filters.employment_status, employmentStatusOptions]);

  useEffect(() => {
    if (!filters.state_id) setSelectedState(null);
    if (!filters.district_id) setSelectedDistrict(null);
  }, [filters]);

  if (!isOpen) return null;

  const handleInputChange = (field: keyof Filters, value: string) => {
    onFiltersChange((prev) => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const handleSelectChange = (field: keyof Filters, option: SelectOption | null) => {
    onFiltersChange((prev) => ({
      ...prev,
      [field]: option ? option.value.toString() : undefined
    }));
  };

  const handleStateSelect = (option: SelectOption | null) => {
    setSelectedState(option);
    setSelectedDistrict(null);

    onFiltersChange((prev) => ({
      ...prev,
      state_id: option ? option.value.toString() : undefined,
      district_id: undefined
    }));
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiSearch },
    { id: 'role', label: 'Role & Status', icon: FiUser },
    { id: 'location', label: 'Location', icon: FiMapPin },
    { id: 'dates', label: 'Dates', icon: FiCalendar }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-visible">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" />
              Filters
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                ? 'border-blue-800 text-blue-900 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Name, Email, or Username"
                  value={filters.search || ''}
                  onChange={(e) => handleInputChange('search', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Full Name"
                  value={filters.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Email Address"
                  value={filters.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Username"
                  value={filters.username || ''}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employee Code</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Employee Code"
                  value={filters.employee_code || ''}
                  onChange={(e) => handleInputChange('employee_code', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  placeholder="Phone Number"
                  value={filters.phone_number || ''}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Role & Status Tab */}
          {activeTab === 'role' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <SelectWithSearch
                  placeholder="All Roles"
                  value={selectedRole}
                  onChange={(option) => handleSelectChange('role_id', option)}
                  options={roleOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <SelectWithSearch
                  placeholder="All Status"
                  value={selectedStatus}
                  onChange={(option) => handleSelectChange('status', option)}
                  options={statusOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
                <SelectWithSearch
                  placeholder="All Employment Status"
                  value={selectedEmploymentStatus}
                  onChange={(option) => handleSelectChange('employment_status', option)}
                  options={employmentStatusOptions}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  value={filters.association_type || ''}
                  onChange={(e) => handleInputChange('association_type', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="inhouse">{getAssociationDisplayLabel('inhouse')}</option>
                  <option value="vendor">{getAssociationDisplayLabel('vendor')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is Field User</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  value={filters.is_field_user || ''}
                  onChange={(e) => handleInputChange('is_field_user', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  value={filters.gender || ''}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State ID</label>
                <SelectWithSearch
                  placeholder="Select State"
                  value={selectedState}
                  onChange={handleStateSelect}
                  apiEndpoint="/state-circle/list"
                  dataKey="states"
                  labelKey="state_name"
                  valueKey="id"
                  searchParam="search"
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">District ID</label>
                <SelectWithSearch
                  placeholder="Select District"
                  value={selectedDistrict}
                  onChange={(option) => {
                    setSelectedDistrict(option);
                    handleSelectChange('district_id', option);
                  }}
                  apiEndpoint="/state-circle"
                  dataKey="districts"
                  labelKey="name"
                  valueKey="id"
                  searchParam="search"
                  dependencyId={selectedState ? Number(selectedState.value) : null}
                  className="w-full"
                  closeOnOutsideClick={false}
                />
              </div>
            </div>
          )}

          {/* Dates Tab */}
          {activeTab === 'dates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created From</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  value={filters.created_from || ''}
                  onChange={(e) => handleInputChange('created_from', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created To</label>
                <input
                  type="date"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                  value={filters.created_to || ''}
                  onChange={(e) => handleInputChange('created_to', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset Filters
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-800 transition-colors flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ViewMoreProjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const ViewMoreProjectsModal: React.FC<ViewMoreProjectsModalProps> = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4 transition-all duration-300">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col overflow-hidden transform transition-all scale-100 border border-gray-100">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-blue-800 to-blue-900 flex justify-between items-center">
          <div className="flex flex-col">
            <h3 className="text-lg font-bold text-white leading-tight">
              Assigned Projects
            </h3>
            <p className="text-blue-100 text-xs mt-0.5">
              {user.first_name} {user.last_name} ({user.employee_code})
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-200 group"
          >
            <FiX className="w-5 h-5 group-hover:scale-110 transition-transform" />
          </button>
        </div>
        <div className="p-7 overflow-y-auto">
          <div className="grid grid-cols-1 gap-2.5">
            {user.user_projects?.map((up) => (
              <div 
                key={up.id} 
                className="flex items-center px-4 py-3 rounded-lg bg-indigo-50/50 text-indigo-700 border border-indigo-100/50 hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-200"
              >
                <div className="w-2 h-2 rounded-full bg-indigo-500 mr-3 shadow-sm shadow-indigo-200" />
                <span className="text-sm font-semibold tracking-wide">
                  {up.project?.project_name || `#${up.project_id}`}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-5 border-t border-gray-100 bg-gray-50/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-100 transition-all duration-200 shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

interface Pagination {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface UsersListProps {
  users: User[];
  pagination: any;
  roles: Role[];
  onSearch: (filters: any) => void;
  onSort: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onAddUser: () => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onViewUser?: (user: User) => void;
  loading?: boolean;
  appliedFilters?: Filters;
  sortState?: { sort_by: string; sort_order: 'ASC' | 'DESC' };
  title?: string;
}

export const UsersList: React.FC<UsersListProps> = ({
  users,
  pagination,
  roles,
  onSearch,
  onSort,
  onPageChange,
  onLimitChange,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onViewUser,
  loading = false,
  appliedFilters,
  sortState,
  title = 'User Management'
}) => {
  const [filters, setFilters] = useState<Filters>(appliedFilters || {});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { hasPermission } = useAuth();

  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>({
    field: 'first_name',
    direction: 'ASC'
  });

  const [selectedRoleFilter, setSelectedRoleFilter] = useState<{ value: string | number; label: string } | null>(null);
  const [selectedUserForProjects, setSelectedUserForProjects] = useState<User | null>(null);

  useEffect(() => {
    if (appliedFilters) {
      setFilters(appliedFilters);
    } else {
      setFilters({});
    }
  }, [appliedFilters]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onSearch(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSelectedRoleFilter(null);
    onSearch({});
  };

  const getActiveFiltersCount = () => {
    return Object.keys(filters).filter(key => filters[key as keyof Filters]).length;
  };

  const handleApplyFilters = () => {
    onSearch(filters);
  };

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ?
      <FaSortUp className="w-3 h-3" /> :
      <FaSortDown className="w-3 h-3" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  enum UserStatus {
    INITIATED = 'initiated',
    PENDING = 'pending',
    VERIFIED = 'verified',
    SUSPENDED = 'suspended',
    DEACTIVATED = 'deactivated'
  }

  const getStatusBadge = (status: UserStatus, emailVerified: boolean) => {
    switch (status) {
      case UserStatus.INITIATED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <FaClock className="w-3 h-3" />
            Initiated
          </span>
        );
      case UserStatus.PENDING:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaClock className="w-3 h-3" />
            Pending
          </span>
        );
      case UserStatus.VERIFIED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="w-3 h-3" />
            Verified
          </span>
        )
      case UserStatus.SUSPENDED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FaTimesCircle className="w-3 h-3" />
            Suspended
          </span>
        );
      case UserStatus.DEACTIVATED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <FaTimesCircle className="w-3 h-3" />
            Deactivated
          </span>
        );
      default:
        return null;
    }
  }

  const getEmploymentStatusBadge = (status: string) => {
    const config: Record<string, { bgColor: string; textColor: string; label: string }> = {
      working: { bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Working' },
      not_working: { bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Not Working' },
      left: { bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Left' },
    };

    const s = config[status] || { bgColor: 'bg-gray-100', textColor: 'text-gray-800', label: status };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${s.bgColor} ${s.textColor}`}>
        {s.label}
      </span>
    );
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0)}${lastName?.charAt(0)}`.toUpperCase();
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {hasPermission('Team', 'create') && (
              <>
                <button
                  onClick={() => setIsImportModalOpen(true)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300 px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm w-full sm:w-auto"
                >
                  <FaCloudUploadAlt className="w-4 h-4 text-blue-600" />
                  Import Users
                </button>
                <button
                  onClick={onAddUser}
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm w-full sm:w-auto"
                >
                  <FaPlus className="w-4 h-4" />
                  Add New User
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Quick Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Quick search by Name, Email or Username..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>

          {/* Advanced Filter Button */}
          <div className="w-full sm:w-auto">
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
            >
              <FiFilter className="w-4 h-4" />
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Active Filters:</span>
              {Object.entries(filters).map(([key, value]) =>
                value && (
                  <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {key.replace(/_/g, ' ')}: {value}
                    <button
                      onClick={() => {
                        const newFilters = { ...filters };
                        delete newFilters[key as keyof Filters];
                        setFilters(newFilters);
                        onSearch(newFilters);
                      }}
                      className="ml-1 text-blue-900 hover:text-blue-800"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </span>
                )
              )}
              <button
                onClick={handleResetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        roles={roles}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Avatar
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('employee_code')}
              >
                <div className="flex items-center gap-1">
                  Employee Code
                  {getSortIcon('employee_code')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('first_name')}
              >
                <div className="flex items-center gap-1">
                  First Name
                  {getSortIcon('first_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('last_name')}
              >
                <div className="flex items-center gap-1">
                  Last Name
                  {getSortIcon('last_name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('username')}
              >
                <div className="flex items-center gap-1">
                  Username
                  {getSortIcon('username')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Device Version
              </th>

              {/* <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[180px]"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-1">
                  Email
                  {getSortIcon('email')}
                </div>
              </th> */}
              {/* <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('phone_number')}
              >
                <div className="flex items-center gap-1">
                  Phone
                  {getSortIcon('phone_number')}
                </div>
              </th> */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Department/Project Group
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                Projects
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon('status')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[120px]"
                onClick={() => handleSort('employment_status')}
              >
                <div className="flex items-center gap-1">
                  Employment Status
                  {getSortIcon('employment_status')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Designation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[220px]">
                Reporting Person
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[100px]"
                onClick={() => handleSort('city')}
              >
                <div className="flex items-center gap-1">
                  City
                  {getSortIcon('city')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                State
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[100px]"
                onClick={() => handleSort('country')}
              >
                <div className="flex items-center gap-1">
                  Country
                  {getSortIcon('country')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[100px]"
                onClick={() => handleSort('pincode')}
              >
                <div className="flex items-center gap-1">
                  Pincode
                  {getSortIcon('pincode')}
                </div>
              </th>

              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors min-w-[150px]"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon('created_at')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={19} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading users...</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={19} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaSearch className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No users found matching your criteria</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission('Team', 'view') && (
                        <Link
                          target="_blank"
                          to={`/team/${user.id}`}
                          className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded transition-colors"
                          title="View user details"
                        >
                          <FaEye className="w-4 h-4" />
                        </Link>
                      )}
                      {hasPermission('Team', 'edit') && (
                        <button
                          onClick={() => onEditUser(user)}
                          className="text-blue-900 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit user"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {/* {hasPermission('Team', 'delete') && (
                        <button
                          onClick={() => onDeleteUser(user)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Delete user"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )} */}
                    </div>
                  </td>

                  {/* Avatar */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex-shrink-0">
                      {user.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt={`${user.first_name} ${user.last_name}`}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-medium text-sm ring-2 ring-gray-200">
                          {getInitials(user.first_name, user.last_name)}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Employee Code */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {user.employee_code}
                    </span>
                  </td>

                  {/* First Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {user.first_name}
                    </span>
                  </td>

                  {/* Last Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">
                      {user.last_name}
                    </span>
                  </td>

                  {/* Username */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      @{user.username}
                    </span>
                  </td>

                  {/* Device Version */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      {user.device_version || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Email */}
                  {/* <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900" title={user.email}>
                        {user.email}
                      </span>
                    </div>
                  </td> */}

                  {/* <td className="px-4 py-4 whitespace-nowrap">
                    {user.phone_number ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">
                          {user.country_code ? (user.country_code.startsWith('+') ? user.country_code : `+${user.country_code}`) : '+91'} {user.phone_number}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No phone</span>
                    )}
                  </td> */}

                  {/* Role */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {user.role.name}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {user.department?.name || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    <div className="flex flex-wrap gap-1.5 max-w-[280px] items-center">
                      {user.user_projects && user.user_projects.length > 0
                        ? (
                          <>
                            {user.user_projects.slice(0, 2).map((up) => (
                              <span 
                                key={up.id} 
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-100/50 shadow-sm"
                                title={up.project?.project_name}
                              >
                                {up.project?.project_name || `#${up.project_id}`}
                              </span>
                            ))}
                            {user.user_projects.length > 2 && (
                              <button
                                onClick={() => setSelectedUserForProjects(user)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-gray-50 text-gray-600 border border-dashed border-gray-300 hover:bg-blue-50 hover:text-blue-700 hover:border-solid hover:border-blue-200 transition-all duration-200 group shadow-sm"
                                title="View all projects"
                              >
                                <FaEye className="w-3 h-3 group-hover:scale-110 transition-transform" />
                                <span>+{user.user_projects.length - 2} more</span>
                              </button>
                            )}
                          </>
                        )
                        : <span className="text-gray-400 italic text-xs">No projects assigned</span>
                      }
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getStatusBadge(user.status as UserStatus, user.email_verified)}
                  </td>

                  {/* Employment Status */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getEmploymentStatusBadge(user.employment_status)}
                  </td>

                  {/* Designation */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {<span className="text-gray-400 italic">{user?.designation?.name || 'N/A'}</span>}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[220px]">
                    <div className="text-sm text-gray-900">
                      {`${user.project_manager?.first_name ?? ''} ${user.project_manager?.last_name ?? ''}`.trim() || <span className="text-gray-400 italic">N/A</span>}
                    </div>
                    {user.project_manager?.designation?.name && (
                      <div className="text-xs text-gray-500">{user.project_manager.designation.name}</div>
                    )}
                    {user.project_manager?.employee_code && (
                      <div className="text-xs text-gray-500">Code: {user.project_manager.employee_code}</div>
                    )}
                  </td>

                  {/* City */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {user.city || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* State */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {typeof user.state === 'object' && user.state !== null
                        ? user.state.state_name
                        : user.state || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Country */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {user.country || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Pincode */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {user.pincode || <span className="text-gray-400 italic">N/A</span>}
                    </span>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {formatDate(user.created_at)}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination removed — infinite scroll handled by parent */}
       <Pagination
        pagination={pagination}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />

      {/* Import Users Modal */}
      <ImportUsersOffcanvas
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          setIsImportModalOpen(false);
          onSearch({});
        }}
      />

      <ViewMoreProjectsModal
        isOpen={selectedUserForProjects !== null}
        onClose={() => setSelectedUserForProjects(null)}
        user={selectedUserForProjects}
      />
    </div>
  );
};
